import { useContext } from 'react';

import { useProductUrls } from '@lucid/hooks';
import { getI18nData, isBrowser } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import { LayoutContext } from '@lucid/components';

import type { Lucid } from 'types/types';
import useDefaultPageUrls from './use-default-page-urls';

export const placeholderButtons: Lucid.Directus.Generated.Menu_Link[] = [
  {
    id: 'placeholder',
    final_url: '#placeholder',
    default_link_text: 'Start Free trial',
    display_as_button: true,
  },
];

export const placeholderLinks: Lucid.Directus.Generated.Menu_Link[] = [
  {
    id: 'placeholder-link',
    final_url: '#placeholder-link',
    default_link_text: 'Log in',
    display_as_button: true,
  },
];

const useNavButtons = (
  username?: string,
  shouldHideSignUpButton = false,
): {
  topNavButtons: Lucid.Directus.Generated.Menu_Link[];
  navButtons: Lucid.Directus.Generated.Menu_Link[];
} => {
  const { urlLangPrefix, shortCode } = useContext(LayoutContext);
  const { i18nData } = useStaticQuery<Lucid.Directus.Custom.DirectusData>(graphql`
    query useNavButtonsQuery {
      i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
        edges {
          node {
            name
            childSrcJson {
              mktg_nav_log_out
              mktg_nav_documents
              mktg_nav_start_free_trial
              mktg_nav_log_in
              mktg_nav_account
              mktg_nav_contact_sales
              mktg_nav_sign_up_free
            }
          }
        }
      }
    }
  `);
  const { activateUrl, pricingUrl, productUrl } = useProductUrls(urlLangPrefix);
  const { contact_sales_url } = useDefaultPageUrls(shortCode);

  if (!isBrowser()) {
    return {
      topNavButtons: [],
      navButtons: [],
    };
  }

  const i18n = getI18nData(i18nData, shortCode);
  const topLevelInitialLoggedInButtons: Lucid.Directus.Generated.Menu_Link[] = [
    {
      id: 'log-out',
      final_url: `${productUrl}/users/logout`,
      default_link_text: i18n('mktg-nav-log-out'),
      beacon_id: 'log-out',
    },
  ];

  const topLevelInitialLoggedOutButtons: Lucid.Directus.Generated.Menu_Link[] = [
    {
      id: 'log-in',
      final_url: activateUrl,
      default_link_text: i18n('mktg-nav-log-in'),
      beacon_id: 'log-in',
    },
  ];

  const navButtonsShared = [
    {
      id: 'contact-sales',
      final_url: contact_sales_url,
      display_as_button: false,
      default_link_text: i18n('mktg-nav-contact-sales'),
      beacon_id: 'contact-sales',
    },
  ];

  const loggedInButtons: Lucid.Directus.Generated.Menu_Link[] = [
    ...navButtonsShared,
    {
      id: 'documents',
      final_url: `${productUrl}/documents`,
      default_link_text: i18n('mktg-nav-documents'),
      beacon_id: 'documents',
      display_as_button: true,
    },
  ];

  const initialLoggedOutButtons: Lucid.Directus.Generated.Menu_Link[] = [
    ...navButtonsShared,
    {
      id: 'sign-up-free',
      final_url: pricingUrl,
      default_link_text: i18n('mktg-nav-sign-up-free'),
      beacon_id: 'sign-up-free',
      display_as_button: true,
    },
  ];

  let loggedOutButtons = initialLoggedOutButtons;
  if (shouldHideSignUpButton) {
    loggedOutButtons = initialLoggedOutButtons.slice(0, 1);
  }

  return username
    ? {
        topNavButtons: topLevelInitialLoggedInButtons,
        navButtons: loggedInButtons,
      }
    : {
        topNavButtons: topLevelInitialLoggedOutButtons,
        navButtons: loggedOutButtons,
      };
};

export default useNavButtons;
